import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  BookOpen, 
  Clock, 
  Eye,
  Search,
  ArrowRight,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [postsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/blog/posts`),
        axios.get(`${API}/blog/categories`)
      ]);
      setPosts(postsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch blog data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesCategory = activeCategory === 'all' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="blog-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-[#00FF94]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-white">
                Privacy Blog
              </h1>
              <p className="text-gray-500">
                Learn how to protect your digital privacy
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-md mt-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles..."
              className="pl-10 bg-[#0a0a0a] border-[#262626] text-white placeholder:text-gray-600"
              data-testid="blog-search"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <h3 className="font-heading font-semibold text-white mb-4">Categories</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${
                    activeCategory === 'all'
                      ? 'bg-[#00FF94] text-black font-medium'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid="category-all"
                >
                  All Posts
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors flex justify-between items-center ${
                      activeCategory === cat.id
                        ? 'bg-[#00FF94] text-black font-medium'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <span>{cat.name}</span>
                    <span className={`text-xs ${activeCategory === cat.id ? 'text-black/60' : 'text-gray-600'}`}>
                      {cat.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Newsletter CTA */}
              <div className="mt-8 p-4 bg-[#0a0a0a] border border-[#262626] rounded-xl">
                <h4 className="font-medium text-white mb-2">Stay Updated</h4>
                <p className="text-sm text-gray-500 mb-3">Get privacy tips in your inbox</p>
                <Link to="/?subscribe=true">
                  <button className="w-full btn-primary py-2 text-sm rounded-lg">
                    Subscribe Free
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Posts Grid */}
          <div className="lg:col-span-3">
            {filteredPosts.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">No articles found</h3>
                <p className="text-gray-500 text-sm">Try a different search or category</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredPosts.map((post, index) => (
                  <Link to={`/blog/${post.slug}`} key={post.id}>
                    <Card 
                      className="bg-[#0a0a0a] border-[#262626] overflow-hidden group card-hover h-full"
                      data-testid={`blog-card-${index}`}
                    >
                      <div className="aspect-video relative overflow-hidden">
                        <img 
                          src={post.image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800'} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                        <Badge className="absolute top-3 right-3 bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20 capitalize">
                          {post.category}
                        </Badge>
                      </div>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(post.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.views?.toLocaleString()} views
                          </span>
                        </div>
                        <h2 className="font-heading font-semibold text-white text-lg mb-2 group-hover:text-[#00FF94] transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center gap-1 text-sm text-[#00FF94] font-medium">
                          Read more
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
